import {
  CapacityAllocationRepetitionTypeEnum,
  CapacityAllocationTypeEnum,
  CapacityAllocationWorkStatus,
  type Maybe,
  type PlanningDailyViewPeopleQuery,
} from "@src/__generated__/graphql";
import { getUsersPlannableCapacityForDate } from "@src/utils/getUsersPlannableCapacity";
import type { Time } from "@src/utils/types";
import { isEqual, sumBy } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";

export type TPlanningDailyViewUserByDay = NonNullable<
  PlanningDailyViewPeopleQuery["planningDailyViewPeople"]
>["data"][0];

type TPlanningDailyViewUserDay = TPlanningDailyViewUserByDay["items"][0];
type TPlanningDailyViewItem = NonNullable<
  TPlanningDailyViewUserDay["items"]
>[0];
type TPlanningDailyViewItemUserStats = NonNullable<
  TPlanningDailyViewItem["userStats"]
>;
type TPlanningDailyViewItemStats = NonNullable<TPlanningDailyViewItem["stats"]>;

export function hasSameItems<T>(a: readonly T[], b: readonly T[]): boolean {
  return a.length === b.length && a.every((item, index) => item === b[index]);
}

function getContainerId(userId: string, date: string): string {
  return `${userId}::${new Date(date).toDateString()}`;
}

export class PlanningDailyViewUserByDayModel {
  @observable.ref user: TPlanningDailyViewUserByDay["user"];
  @observable.ref items: PlanningDailyViewUserDayModel[];
  constructor(src: TPlanningDailyViewUserByDay) {
    makeObservable(this);
    this.user = src.user;
    this.items = src.items.map(
      (item) => new PlanningDailyViewUserDayModel(item, src.user),
    );
  }

  // Keeps unchanged days and items, so a sync re-renders only what changed
  @action update(src: TPlanningDailyViewUserByDay): void {
    if (!isEqual(this.user, src.user)) this.user = src.user;

    const daysByContainerId = new Map(
      this.items.map((day) => [day.containerId, day]),
    );
    const items = src.items.map((daySrc) => {
      const day = daysByContainerId.get(
        getContainerId(this.user.id, daySrc.date),
      );
      if (!day) return new PlanningDailyViewUserDayModel(daySrc, this.user);

      day.update(daySrc, this.user);
      return day;
    });

    if (!hasSameItems(items, this.items)) this.items = items;
  }
}

export class PlanningDailyViewUserDayModel {
  @observable.ref capacity: TPlanningDailyViewUserDay["capacity"];
  @observable.ref workStatus: TPlanningDailyViewUserDay["workStatus"];
  @observable.ref date: TPlanningDailyViewUserDay["date"];
  @observable.ref holiday: TPlanningDailyViewUserDay["holiday"];
  @observable items: PlanningDailyViewItemModel[];
  @observable.ref user: TPlanningDailyViewUserByDay["user"];
  @observable.ref
  total_tracked_for_date: TPlanningDailyViewUserDay["total_tracked_for_date"];
  containerId: string;

  constructor(
    src: TPlanningDailyViewUserDay,
    user: TPlanningDailyViewUserByDay["user"],
  ) {
    makeObservable(this);
    this.user = user;
    this.containerId = getContainerId(user.id, src.date);
    this.capacity = src.capacity;
    this.workStatus = src.workStatus;
    this.date = src.date;
    this.items =
      src.items?.map((item) => new PlanningDailyViewItemModel(item, user)) ??
      [];
    this.holiday = src.holiday;
    this.total_tracked_for_date = src.total_tracked_for_date;
  }

  @action update(
    src: TPlanningDailyViewUserDay,
    user: TPlanningDailyViewUserByDay["user"],
  ): void {
    this.user = user;
    this.capacity = src.capacity;
    this.workStatus = src.workStatus;
    this.date = src.date;
    this.holiday = src.holiday;
    this.total_tracked_for_date = src.total_tracked_for_date;

    const itemsById = new Map(this.items.map((item) => [item.id, item]));
    const items =
      src.items?.map((itemSrc) => {
        const item = itemsById.get(itemSrc.id);
        if (item?.isSameAs(itemSrc, user)) return item;

        return new PlanningDailyViewItemModel(itemSrc, user);
      }) ?? [];

    if (!hasSameItems(items, this.items)) this.items = items;
  }

  @computed get sortableItems(): PlanningDailyViewItemModel[] {
    return this.items.filter(
      (item) => item.type !== CapacityAllocationTypeEnum.HomeOffice,
    );
  }

  @computed get sortableItemIds(): PlanningDailyViewItemModel["id"][] {
    return this.sortableItems.map(({ id }) => id);
  }

  @computed get plannedTime(): number {
    return sumBy(this.items, (userItem) => {
      const { computedTotalTime, total_time, type, stats } = userItem;
      const totalTime = computedTotalTime ?? total_time;

      if (type === CapacityAllocationTypeEnum.HomeOffice) return 0;
      if (type !== CapacityAllocationTypeEnum.TimeOff) return totalTime;
      if (stats?.time_off_type_affects_planning_capacity) return totalTime;
      return 0;
    });
  }

  @computed get calculatedCapacity(): number {
    if (!this.user.profile?.planningRanges) return 0;
    const capacity = getUsersPlannableCapacityForDate(
      this.user.profile.planningRanges,
      new Date(this.date),
    );

    if (
      this.workStatus === CapacityAllocationWorkStatus.Unavailable ||
      this.workStatus === CapacityAllocationWorkStatus.WithoutCapacity
    )
      return 0;

    return capacity - this.plannedTime;
  }

  @computed get calculatedWorkStatus(): CapacityAllocationWorkStatus {
    if (this.workStatus === CapacityAllocationWorkStatus.WithoutCapacity) {
      return CapacityAllocationWorkStatus.WithoutCapacity;
    }
    if (this.workStatus === CapacityAllocationWorkStatus.Unavailable) {
      return CapacityAllocationWorkStatus.Unavailable;
    }
    const onlyTimeOffs =
      !!this.items.length &&
      this.items.every(
        (userItem) =>
          (userItem.type === CapacityAllocationTypeEnum.TimeOff &&
            userItem.stats?.time_off_type_affects_planning_capacity) ||
          userItem.type === CapacityAllocationTypeEnum.HomeOffice,
      );
    if (onlyTimeOffs && this.calculatedCapacity <= 0) {
      return CapacityAllocationWorkStatus.TimeOff;
    }
    return CapacityAllocationWorkStatus.Working;
  }
}

type PlanningDailyViewItemUser = Pick<
  TPlanningDailyViewUserByDay["user"],
  "id" | "full_name" | "image"
> & {
  profile?: Maybe<
    Pick<
      NonNullable<TPlanningDailyViewUserByDay["user"]["profile"]>,
      "hex_color"
    >
  >;
};
export class PlanningDailyViewItemModel {
  @observable.ref id: TPlanningDailyViewItem["id"];
  @observable.ref user: PlanningDailyViewItemUser;
  @observable.ref type: TPlanningDailyViewItem["type"];
  @observable.ref total_time: TPlanningDailyViewItem["total_time"];
  @observable.ref
  time_tracking_work_type_id: TPlanningDailyViewItem["time_tracking_work_type_id"];
  @observable.ref completed: TPlanningDailyViewItem["completed"];
  tracked_for_item: TPlanningDailyViewItemUserStats["tracked_time_for_item"];
  @observable.ref
  has_running_timer: TPlanningDailyViewItemUserStats["has_running_timer"];
  @observable.ref stats?: TPlanningDailyViewItemStats;
  @observable.ref from_time: Maybe<Time>;
  @observable.ref to_time: Maybe<Time>;
  @observable.ref date: TPlanningDailyViewItem["date"];
  @observable.ref note: TPlanningDailyViewItem["note"];
  @observable.ref createdBy: TPlanningDailyViewItem["createdBy"];
  @observable.ref
  teams: NonNullable<TPlanningDailyViewItem["teams"]>[0]["team"][];
  @observable.ref isRepeating: boolean;
  @observable global: TPlanningDailyViewItem["global"];

  @observable computedTotalTime?: number = undefined;

  // The API data the item was built from. Cleared on a local change, so the next sync replaces the item.
  private src: TPlanningDailyViewItem | undefined;

  constructor(src: TPlanningDailyViewItem, user: PlanningDailyViewItemUser) {
    makeObservable(this);
    this.src = src;
    this.id = src.id;
    this.user = user;
    this.total_time = src.total_time;
    this.type = src.type;
    this.time_tracking_work_type_id = src.time_tracking_work_type_id;
    this.completed = src.completed;
    this.tracked_for_item = src.userStats?.tracked_time_for_item;
    this.has_running_timer = src.userStats?.has_running_timer ?? false;
    this.stats = src.stats ?? undefined;
    // NOTE: Apollo sends time with milliseconds and we don't need those here
    this.from_time = src.from_time?.slice(0, 5) as Maybe<Time>;
    this.to_time = src.to_time?.slice(0, 5) as Maybe<Time>;
    this.date = src.date;
    this.note = src.note;
    this.createdBy = src.createdBy;
    this.teams = src.teams?.map(({ team }) => ({ id: team.id })) ?? [];
    this.global = src.global;
    this.isRepeating =
      this.global ||
      this.teams.length > 0 ||
      this.stats?.repetition !== CapacityAllocationRepetitionTypeEnum.Once ||
      this.stats.items_count > 1;
  }

  isSameAs(
    src: TPlanningDailyViewItem,
    user: PlanningDailyViewItemUser,
  ): boolean {
    return this.user === user && isEqual(this.src, src);
  }

  @action applyLocalChanges(changes: PlanningDailyViewItemLocalChanges): void {
    Object.assign(this, changes);
    this.src = undefined;
  }

  setHasRunningTimer(hasRunningTimer: boolean): void {
    this.applyLocalChanges({ has_running_timer: hasRunningTimer });
  }
}

type PlanningDailyViewItemLocalChanges = Partial<
  Pick<
    PlanningDailyViewItemModel,
    | "id"
    | "user"
    | "completed"
    | "time_tracking_work_type_id"
    | "has_running_timer"
    | "total_time"
    | "to_time"
    | "computedTotalTime"
  >
>;
